import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, map } from 'rxjs';
import { CustomHttpClientService } from 'src/app/infrastructure/services/custom-http-client.service';
import { IncidentCategorySearchFilters } from '../models/IncidentCategorySearchFilters';
import { IncidentCategoryCollectionDto } from '../models/IncidentCategoryCollectionDto';
import { PageContentSearchFilters } from '../models/PageContentSearchFilters';
import { PageContentCollectionDto } from '../models/PageContentCollectionDto';
import { ConfigSearchFilters } from '../models/ConfigSearchFilters';
import { ConfigCollectionDto } from '../models/ConfigCollectionDto';
import { PageContentDto } from '../models/PageContentDto';
import { ConfigDto } from '../models/ConfigDto';
import { IncidentCategoryDto } from '../models/IncidentCategoryDto';
import {
  InformationCollectionDto,
  InformationSearchFilters,
} from '../models/InformationDtos';
@Injectable({
  providedIn: 'root',
})
export class PortalSettingService {
  public isShowTabView$ = new BehaviorSubject<boolean>(true);
  public readonly InformationUrl = 'api/admin-settings/information';
  constructor(private http: CustomHttpClientService) {}

  async GetDataRetentionList() {
    return lastValueFrom(
      this.http
        .get(`api/admin-settings/document-template/retention-settings`)
        .pipe(map((data: any) => data)),
    );
  }

  GetImpactedEnvelops(docId: number, retentionDays: number) {
    return lastValueFrom(
      this.http
        .get(
          `api/admin-settings/document-template/retention-settings/${docId}/impacted-envelopes?retentionDays=${retentionDays}`,
        )
        .pipe(map((data: any) => data)),
    );
  }

  UpdateDataRetention(data: any[]) {
    return lastValueFrom(
      this.http
        .put(`api/admin-settings/document-template/retention-settings`, data)
        .pipe(map((data: any) => data)),
    );
  }

  async GetCategoryList(
    filters: IncidentCategorySearchFilters,
    skip: number,
    take: number,
  ): Promise<IncidentCategoryCollectionDto> {
    return lastValueFrom(
      this.http.post<IncidentCategoryCollectionDto>(
        `api/admin-settings/category-list?skip=${skip}&take=${take}`,
        filters,
      ),
    );
  }

  async GetPageContentList(
    filters: PageContentSearchFilters,
    skip: number,
    take: number,
  ): Promise<PageContentCollectionDto> {
    return lastValueFrom(
      this.http.post<PageContentCollectionDto>(
        `api/admin-settings/page-content-list?skip=${skip}&take=${take}`,
        filters,
      ),
    );
  }

  async GetInformationList(
    filters: InformationSearchFilters,
    skip: number,
    take: number,
  ): Promise<InformationCollectionDto> {
    return lastValueFrom(
      this.http.post<InformationCollectionDto>(
        `${this.InformationUrl}?skip=${skip}&take=${take}`,
        filters,
      ),
    );
  }

  async GetConfigs(
    filters: ConfigSearchFilters,
    skip: number,
    take: number,
  ): Promise<ConfigCollectionDto> {
    return lastValueFrom(
      this.http.post<ConfigCollectionDto>(
        `api/admin-settings/module-config-list?skip=${skip}&take=${take}`,
        filters,
      ),
    );
  }

  async GetPageContentAsync(pageContentId: number): Promise<PageContentDto> {
    return lastValueFrom(
      this.http.get<PageContentDto>(
        `api/admin-settings/page-content/${pageContentId}`,
      ),
    );
  }
  async GetById<T>(url: string, id: number): Promise<T> {
    return lastValueFrom(this.http.get<T>(`${url}/${id}`));
  }
  async Update<T>(url: string, id: number, item: T): Promise<T> {
    return lastValueFrom(this.http.patch<T>(`${url}/${id}`, item));
  }
  async DeactivatePageContentAsync(pageContentId: number) {
    return lastValueFrom(
      this.http.patch(
        `api/admin-settings/page-content/deactivate/${pageContentId}`,
        null,
      ),
    );
  }

  async ReactivatePageContentAsync(pageContentId: number) {
    return lastValueFrom(
      this.http.patch(
        `api/admin-settings/page-content/reactivate/${pageContentId}`,
        null,
      ),
    );
  }

  async UpdatePageContentAsync(pageContentDto: PageContentDto): Promise<any> {
    return lastValueFrom(
      this.http.patch<any>(
        `api/admin-settings/page-content/${pageContentDto.id}`,
        pageContentDto,
      ),
    );
  }

  async UpdateConfigStatusAsync(configId: number, status: boolean) {
    return lastValueFrom(
      this.http.patch(
        `api/admin-settings/module-config/update/${configId}/status`,
        status,
      ),
    );
  }

  async UpdateConfigDetailsAsync(data: ConfigDto) {
    return lastValueFrom(
      this.http.post(`api/admin-settings/module-config/update`, data),
    );
  }

  async CreateCategoryAsync(data: IncidentCategoryDto) {
    return lastValueFrom(
      this.http.post(`api/admin-settings/category/save`, data),
    );
  }

  async UpdateCategoryAsync(status: boolean, categoryId: number) {
    return lastValueFrom(
      this.http.patch(
        `api/admin-settings/category/update/${categoryId}/status/${status}`,
        null,
      ),
    );
  }

  async GetCategoryDetailsAsync(categoryId: number) {
    return lastValueFrom(
      this.http.get<IncidentCategoryDto>(
        `api/admin-settings/category/${categoryId}`,
      ),
    );
  }

  async VerifyDisplayNameAlreadyExistAsync(displayName: string) {
    return lastValueFrom(
      this.http.post(
        'api/admin-settings/category/unique',
        JSON.stringify(displayName),
      ),
    );
  }
}
